import { AdminUser } from '../types';

// Multi-account session store for the admin panel. One entry per logged-in
// member account; everything lives in localStorage so all tabs share the same
// account list and the same active pointer (the active account is per
// browser, Google-style).
export interface StoredSession {
  user: AdminUser;
  org: string | null;
  orgName: string | null;
  accessToken: string;
  // null when the user declined «Запам'ятати мене» — the account then lives
  // only as long as its access token (no silent renewal).
  refreshToken: string | null;
  addedAt: string; // ISO timestamp of the latest login into this account
}

const SESSIONS_KEY = 'admin_sessions';
const ACTIVE_KEY = 'admin_active_member';
// Pre-switcher single-account keys, folded into the store on first read.
const LEGACY_TOKEN_KEY = 'access_token';
const LEGACY_USER_KEY = 'admin_user';

type SessionMap = Record<string, StoredSession>;

function migrateLegacy(): void {
  if (localStorage.getItem(SESSIONS_KEY)) {
    // Already on the multi-account store — drop any stray legacy credentials
    // so an old access_token can't linger in storage forever.
    sessionStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
    return;
  }
  const token =
    sessionStorage.getItem(LEGACY_TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
  const rawUser = localStorage.getItem(LEGACY_USER_KEY);
  if (!token && !rawUser) return;
  try {
    if (token && rawUser) {
      const user: AdminUser = JSON.parse(rawUser);
      if (typeof user?.id === 'number') {
        const map: SessionMap = {
          [String(user.id)]: {
            user,
            org: null,
            orgName: null,
            accessToken: token,
            refreshToken: null,
            addedAt: new Date().toISOString(),
          },
        };
        localStorage.setItem(SESSIONS_KEY, JSON.stringify(map));
        localStorage.setItem(ACTIVE_KEY, String(user.id));
      }
    }
  } catch {
    // corrupt legacy admin_user — drop it below
  }
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(LEGACY_USER_KEY);
}

function readMap(): SessionMap {
  migrateLegacy();
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as SessionMap) : {};
  } catch {
    return {};
  }
}

function writeMap(map: SessionMap): void {
  localStorage.setItem(SESSIONS_KEY, JSON.stringify(map));
}

// The docs site (proxied under /docs) is a separate Next.js app that cannot
// read our localStorage, so the active account's access token is mirrored
// into a cookie. Its edge middleware validates the token against the backend
// and gates the platform-admin section by is_superadmin. Host-only cookie:
// the docs proxy is same-origin, and it must not leak to other subdomains.
const DOCS_COOKIE = 'se_admin_token';
const DOCS_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // token itself expires sooner

export function syncDocsCookie(): void {
  const token = getActiveSession()?.accessToken ?? null;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  if (token) {
    document.cookie = `${DOCS_COOKIE}=${token}; Path=/; Max-Age=${DOCS_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } else {
    document.cookie = `${DOCS_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  }
}

export function listSessions(): StoredSession[] {
  return Object.values(readMap()).sort((a, b) => a.addedAt.localeCompare(b.addedAt));
}

export function getSession(memberId: number): StoredSession | null {
  return readMap()[String(memberId)] ?? null;
}

export function getActiveMemberId(): number | null {
  migrateLegacy();
  const raw = localStorage.getItem(ACTIVE_KEY);
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

export function getActiveSession(): StoredSession | null {
  const id = getActiveMemberId();
  return id === null ? null : getSession(id);
}

export function getActiveAccessToken(): string | null {
  return getActiveSession()?.accessToken ?? null;
}

export function upsertSession(session: StoredSession, makeActive = true): void {
  const map = readMap();
  map[String(session.user.id)] = session;
  writeMap(map);
  if (makeActive) {
    localStorage.setItem(ACTIVE_KEY, String(session.user.id));
  }
  syncDocsCookie();
}

export function updateTokens(
  memberId: number,
  accessToken: string,
  refreshToken?: string | null
): void {
  const map = readMap();
  const s = map[String(memberId)];
  if (!s) return;
  s.accessToken = accessToken;
  if (refreshToken !== undefined) s.refreshToken = refreshToken;
  writeMap(map);
  syncDocsCookie();
}

export function updateUser(memberId: number, user: AdminUser): void {
  const map = readMap();
  const s = map[String(memberId)];
  if (!s) return;
  s.user = user;
  writeMap(map);
}

export function setActive(memberId: number): void {
  localStorage.setItem(ACTIVE_KEY, String(memberId));
  syncDocsCookie();
}

// Removes a session. If it was the active one, promotes the most recently
// added remaining session. Returns the new active member id, or null when no
// sessions remain.
export function removeSession(memberId: number): number | null {
  const map = readMap();
  delete map[String(memberId)];
  writeMap(map);
  const active = localStorage.getItem(ACTIVE_KEY);
  if (active !== String(memberId)) {
    return active ? Number(active) : null;
  }
  const remaining = Object.values(map).sort((a, b) => b.addedAt.localeCompare(a.addedAt));
  if (remaining.length === 0) {
    localStorage.removeItem(ACTIVE_KEY);
    syncDocsCookie();
    return null;
  }
  localStorage.setItem(ACTIVE_KEY, String(remaining[0].user.id));
  syncDocsCookie();
  return remaining[0].user.id;
}

export function removeActiveSession(): number | null {
  const id = getActiveMemberId();
  return id === null ? null : removeSession(id);
}
